import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { createArea, patchArea } from '../api/areas';
import { createRack, patchRack, deleteRack } from '../api/racks';
import { createShelf, patchShelf, deleteShelf } from '../api/shelves';
import { createTable, patchTable, deleteTable } from '../api/tables';
import {
  createCheckoutZone, patchCheckoutZone, deleteCheckoutZone,
  createCheckoutQueue, patchCheckoutQueue,
  createStaffZone, patchStaffZone,
  createPolygonGroup, patchPolygonGroup, deletePolygonGroup,
} from '../api/checkoutControl';
import { loadPolygonData, loadZoneSummary } from '../utils/polygonLoader';
import { normalizePoints } from '../hooks/useCoordinates';
import {
  useAppActions, useAppState,
  PolygonEntityType,
  Rack, Shelf, Table, CheckoutZoneData,
} from '../app/appContext';
import {
  TYPE_OPTIONS, ENTITY_TO_CHECKOUT_TYPE, isCheckoutType, isProjectedType,
} from '../utils/polygonTypes';
import { suggestPerspective, generateShelfGrid, type Pt, type Dims, type GridParams } from '../utils/projection';
import PerspectiveFields from './projection/PerspectiveFields';
import { RACK_TYPE_OPTIONS } from './RackForm';
import CropPreview from './projection/CropPreview';
import BrightenedCropPreview from './projection/BrightenedCropPreview';
import ShelfGridPanel, { DEFAULT_GRID } from './projection/ShelfGridPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Switch } from '@/components/ui/switch';
import ModelSwitchDialog, { type ModelChoice } from '@/pages/Management/ModelSwitchDialog';
import { cn } from '@/lib/utils';

// ── Form State ───────────────────────────────────────────────────────────────

interface FormValues {
  name: string;
  rack_type: string;
  perspective_width: string;
  perspective_height: string;
  initial_width: string;
  initial_height: string;
  is_composite: boolean;
  predict_percentage: boolean;
  single_shelf: boolean;
  /** Полка: '' = как у стеллажа, 'true' = процентная, 'false' = количественная (AI197). */
  shelf_model: '' | 'true' | 'false';
  /** Тип продукции полки поверх стеллажа: '' — как у стеллажа (#3288). */
  shelf_rack_type: string;
  bottom_points_indices: string;
  top_points_indices: string;
  empty_shelf_score: string;
  shelf_num_on_rack: string;
  max_effective_percentage: string;
  max_allowed_size: string;
  max_workers: string;
  /** Столы: снято для закреплённых поверхностей (барные стойки). */
  can_be_physically_moved: boolean;
  /** Столы: «кадр испорчен» только по засвету — полигон всегда накрывает несколько столешниц. */
  spoiled_glare_only: boolean;
  /** Столы: осветление кропа 0..1 (Алексей 26.10, #7770). */
  crop_brighten: number;
  operations_dashboard_id: string;
  merchandising_dashboard_id: string;
  /** Архив стеллажа/стола: скрыт из анализа, обратимо (Эдем 04.11). */
  is_archived: boolean;
}

const defaultValues: FormValues = {
  name: '',
  rack_type: '',
  // Default true: an ordinary table CAN be moved, so the check only has to be
  // cleared for the rare fixed surface.
  can_be_physically_moved: true,
  spoiled_glare_only: false,
  crop_brighten: 0,
  perspective_width: '',
  perspective_height: '',
  initial_width: '',
  initial_height: '',
  is_composite: false,
  predict_percentage: true,
  // Новый стеллаж — переключатель выключен: сначала решают, сколько полок (#1549).
  single_shelf: false,
  shelf_model: '',
  shelf_rack_type: '',
  bottom_points_indices: '2,3',
  top_points_indices: '0,1',
  empty_shelf_score: '0.7',
  shelf_num_on_rack: '',
  max_effective_percentage: '0.8',
  max_allowed_size: '0',
  max_workers: '0',
  operations_dashboard_id: '',
  merchandising_dashboard_id: '',
  is_archived: false,
};

// ── Props ────────────────────────────────────────────────────────────────────

interface UnifiedEditFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialType: PolygonEntityType;
  tempPoints?: [number, number][];
  selectedRackId?: string | null;
  editingRack?: Rack | null;
  editingShelf?: Shelf | null;
  editingTable?: Table | null;
  editingArea?: { id: string; name?: string | null } | null;
  editingCheckoutZone?: CheckoutZoneData | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveAreaId(
  type: PolygonEntityType,
  editingRack?: Rack | null,
  editingShelf?: Shelf | null,
  editingTable?: Table | null,
  editingArea?: { id: string } | null,
  editingCheckoutZone?: CheckoutZoneData | null,
): string | null {
  if (type === 'standalone' && editingArea) return editingArea.id;
  if (type === 'rack' && editingRack) return editingRack.area;
  if (type === 'shelf' && editingShelf) return editingShelf.area;
  if (type === 'table' && editingTable) return editingTable.area;
  if (isCheckoutType(type) && editingCheckoutZone && editingCheckoutZone.areas.length > 0) {
    return editingCheckoutZone.areas[0];
  }
  return null;
}

function resolveEntityId(
  type: PolygonEntityType,
  editingRack?: Rack | null,
  editingShelf?: Shelf | null,
  editingTable?: Table | null,
  editingArea?: { id: string } | null,
  editingCheckoutZone?: CheckoutZoneData | null,
): string | null {
  if (type === 'standalone' && editingArea) return editingArea.id;
  if (type === 'rack' && editingRack) return editingRack.id;
  if (type === 'shelf' && editingShelf) return editingShelf.id;
  if (type === 'table' && editingTable) return editingTable.id;
  if (isCheckoutType(type) && editingCheckoutZone) return editingCheckoutZone.id;
  return null;
}

// ── Field building blocks ────────────────────────────────────────────────────

interface TextFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'number';
  step?: number;
  min?: number;
  max?: number;
  required?: boolean;
  disabled?: boolean;
  helper?: string;
}

function TextField({ label, name, value, onChange, type = 'text', step, min, max, required, disabled, helper }: TextFieldProps) {
  const id = `f-${name}`;
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <Input
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
      />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );
}

function CheckRow({ label, name, checked, onChange }: { label: string; name: string; checked: boolean; onChange: (v: boolean) => void }) {
  const id = `f-${name}`;
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <Label htmlFor={id} className="cursor-pointer">{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function RadioRow({ name, value, current, onChange, label }: { name: string; value: string; current: string; onChange: (v: string) => void; label: string }) {
  const checked = current === value;
  const id = `f-${name}-${value}`;
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 py-1">
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className={cn(
          'size-4 shrink-0 cursor-pointer appearance-none rounded-full border border-border bg-card transition-colors',
          'checked:border-primary checked:border-[5px]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        )}
      />
      <span className="text-sm">{label}</span>
    </label>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

const UnifiedEditForm: React.FC<UnifiedEditFormProps> = ({
  open, onClose, onSuccess, initialType, tempPoints,
  selectedRackId,
  editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone,
}) => {
  const { t } = useT();
  const actions = useAppActions();
  const { camera, polygons, zone, zoneSummary } = useAppState();
  const selectedCameraId = camera.selectedCameraId;
  const frame = polygons.areas.frame;

  const isCreateMode = !editingRack && !editingShelf && !editingTable && !editingArea && !editingCheckoutZone;
  const originalType = initialType;

  const [selectedType, setSelectedType] = useState<PolygonEntityType>(initialType);
  const [form, setForm] = useState<FormValues>(defaultValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachMode, setAttachMode] = useState<'new' | 'existing'>('new');
  const [attachToZoneId, setAttachToZoneId] = useState<string>('');

  const allCheckoutZones = zoneSummary.checkoutZones.length > 0
    ? zoneSummary.checkoutZones
    : Object.values(polygons.checkoutZones.entities);
  const matchingZones = allCheckoutZones.filter(cz => {
    const czType = ENTITY_TO_CHECKOUT_TYPE[selectedType];
    if (!czType || cz.type !== czType) return false;
    if (editingCheckoutZone && cz.id === editingCheckoutZone.id) return false;
    return true;
  });

  // Populate form when opening
  useEffect(() => {
    if (!open) return;
    setSelectedType(initialType);
    setError('');
    setSaving(false);
    setAttachMode('new');
    setAttachToZoneId('');
    setGridArmed(false);
    setGridParams(DEFAULT_GRID);
    setGridError(null);

    if (editingRack) {
      setForm({
        ...defaultValues,
        name: editingRack.name ?? '',
        rack_type: editingRack.rack_type ?? '',
        perspective_width: editingRack.perspective_width != null ? String(editingRack.perspective_width) : '',
        perspective_height: editingRack.perspective_height != null ? String(editingRack.perspective_height) : '',
        initial_width: editingRack.initial_width != null ? String(editingRack.initial_width) : '',
        initial_height: editingRack.initial_height != null ? String(editingRack.initial_height) : '',
        is_composite: Boolean(editingRack.is_composite),
        predict_percentage: Boolean(editingRack.predict_percentage),
        single_shelf: Boolean(editingRack.single_shelf),
        merchandising_dashboard_id: editingRack.merchandising_dashboard_id ?? '',
        is_archived: Boolean(editingRack.is_archived),
      });
    } else if (editingShelf) {
      setForm({
        ...defaultValues,
        name: editingShelf.name ?? '',
        bottom_points_indices: (editingShelf.bottom_points_indices ?? []).join(','),
        top_points_indices: (editingShelf.top_points_indices ?? []).join(','),
        perspective_width: editingShelf.perspective_width != null ? String(editingShelf.perspective_width) : '',
        perspective_height: editingShelf.perspective_height != null ? String(editingShelf.perspective_height) : '',
        empty_shelf_score: editingShelf.empty_shelf_score != null ? String(editingShelf.empty_shelf_score) : '',
        shelf_num_on_rack: editingShelf.shelf_num_on_rack != null ? String(editingShelf.shelf_num_on_rack) : '',
        max_effective_percentage: editingShelf.max_effective_percentage != null ? String(editingShelf.max_effective_percentage) : '0.8',
        shelf_model: editingShelf.predict_percentage == null ? '' : editingShelf.predict_percentage ? 'true' : 'false',
        shelf_rack_type: editingShelf.rack_type ?? '',
      });
    } else if (editingTable) {
      setForm({
        ...defaultValues,
        name: editingTable.name ?? '',
        perspective_width: editingTable.perspective_width != null ? String(editingTable.perspective_width) : '',
        perspective_height: editingTable.perspective_height != null ? String(editingTable.perspective_height) : '',
        // `?? true` and not `!== false`: a table saved before this field existed comes
        // back without it, and such a table is an ordinary movable one.
        can_be_physically_moved: editingTable.can_be_physically_moved ?? true,
        spoiled_glare_only: editingTable.spoiled_glare_only ?? false,
        crop_brighten: editingTable.crop_brighten ?? 0,
        is_archived: editingTable.is_archived ?? false,
      });
    } else if (editingCheckoutZone) {
      setForm({
        ...defaultValues,
        name: editingCheckoutZone.name ?? '',
        max_allowed_size: editingCheckoutZone.queue ? String(editingCheckoutZone.queue.max_allowed_size) : '0',
        max_workers: editingCheckoutZone.staffZone ? String(editingCheckoutZone.staffZone.max_workers) : '0',
        operations_dashboard_id: editingCheckoutZone.operations_dashboard_id ?? '',
      });
    } else if (editingArea) {
      setForm({ ...defaultValues, name: editingArea.name ?? '' });
    } else {
      setForm(defaultValues);
    }
  }, [open, initialType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const setField = (name: keyof FormValues, value: string | boolean) => {
    setForm(prev => ({ ...prev, [name]: value } as FormValues));
  };

  // ── Projection helpers (rack/shelf/table) ──────────────────────────────────

  const [previewNonce, setPreviewNonce] = useState(0);
  const bumpPreview = () => setPreviewNonce(n => n + 1);

  // Points (normalized) for the projected polygon being edited/created, plus the
  // pixel dims its normalized coords map against. Racks scale by initial_*;
  // shelves and tables map against the live frame.
  const projectionInputs = useMemo((): { pointsNorm: Pt[]; pixelDims: Dims } | null => {
    if (!frame) return null;
    const frameDims: Dims = { width: frame.width, height: frame.height };
    let pointsNorm: Pt[] | null = null;
    if (isCreateMode && tempPoints && tempPoints.length === 4) {
      pointsNorm = tempPoints.map(([x, y]) => [x / frame.width, y / frame.height] as Pt);
    } else {
      const aId = resolveAreaId(originalType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone);
      const area = aId ? polygons.areas.entities[aId] : null;
      if (area && area.points.length === 4) pointsNorm = area.points as Pt[];
    }
    if (!pointsNorm) return null;
    let pixelDims = frameDims;
    if (selectedType === 'rack') {
      const iw = Number(form.initial_width) || frame.width;
      const ih = Number(form.initial_height) || frame.height;
      pixelDims = { width: iw, height: ih };
    }
    return { pointsNorm, pixelDims };
  }, [frame, isCreateMode, tempPoints, originalType, selectedType, form.initial_width, form.initial_height,
      editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone, polygons.areas.entities]);

  // Prefill dims when creating a projected polygon: initial_* from frame res
  // (racks), perspective_* from the drawn quad's real side lengths.
  useEffect(() => {
    if (!open || !isCreateMode || !frame) return;
    if (!isProjectedType(selectedType)) return;
    if (!tempPoints || tempPoints.length !== 4) return;
    const pointsNorm: Pt[] = tempPoints.map(([x, y]) => [x / frame.width, y / frame.height]);
    const pixelDims: Dims = { width: frame.width, height: frame.height };
    const sug = suggestPerspective(pointsNorm, pixelDims);
    setForm(prev => ({
      ...prev,
      perspective_width: prev.perspective_width || String(sug.perspective_width),
      perspective_height: prev.perspective_height || String(sug.perspective_height),
      initial_width: selectedType === 'rack' ? (prev.initial_width || String(frame.width)) : prev.initial_width,
      initial_height: selectedType === 'rack' ? (prev.initial_height || String(frame.height)) : prev.initial_height,
    }));
  }, [open, isCreateMode, selectedType, frame, tempPoints]);

  const handleRecalcPerspective = () => {
    if (!projectionInputs) return;
    const sug = suggestPerspective(projectionInputs.pointsNorm, projectionInputs.pixelDims);
    setForm(prev => ({
      ...prev,
      perspective_width: String(sug.perspective_width),
      perspective_height: String(sug.perspective_height),
    }));
    bumpPreview();
  };

  // ── Shelf-grid autopopulate (rack only; new rack or rack with no shelves) ────

  const [gridArmed, setGridArmed] = useState(false);
  // AI197: смена нейросети существующего стеллажа/полки — через предупреждение с 3-сек
  // «Продолжить»; поле меняется в форме, в БД уходит общим «Сохранить».
  const [modelPending, setModelPending] = useState<{ scope: 'rack' | 'shelf'; from: ModelChoice; to: ModelChoice } | null>(null);
  const rackModelSaved: ModelChoice | null = editingRack ? (editingRack.predict_percentage ? 'pct' : 'yolo') : null;
  const shelfModelSaved: ModelChoice | null = editingShelf
    ? (editingShelf.predict_percentage == null ? 'inherit' : editingShelf.predict_percentage ? 'pct' : 'yolo') : null;
  const rackShelvesConfigured = !!editingRack && Object.values(polygons.shelves.entities)
    .some(sh => sh.rack === editingRack.id && sh.predict_percentage != null);
  const pickRackModel = (v: string) => {
    const to: ModelChoice = v === 'true' ? 'pct' : 'yolo';
    const cur: ModelChoice = form.predict_percentage ? 'pct' : 'yolo';
    if (to === cur) return;
    if (rackModelSaved && to !== rackModelSaved) setModelPending({ scope: 'rack', from: cur, to });
    else setField('predict_percentage', to === 'pct');
  };
  const pickShelfModel = (v: string) => {
    const to: ModelChoice = v === '' ? 'inherit' : v === 'true' ? 'pct' : 'yolo';
    const cur: ModelChoice = form.shelf_model === '' ? 'inherit' : form.shelf_model === 'true' ? 'pct' : 'yolo';
    if (to === cur) return;
    if (shelfModelSaved && to !== shelfModelSaved) setModelPending({ scope: 'shelf', from: cur, to });
    else setField('shelf_model', to === 'inherit' ? '' : to === 'pct' ? 'true' : 'false');
  };
  const confirmModel = () => {
    if (!modelPending) return;
    if (modelPending.scope === 'rack') setField('predict_percentage', modelPending.to === 'pct');
    else setField('shelf_model', modelPending.to === 'inherit' ? '' : modelPending.to === 'pct' ? 'true' : 'false');
    setModelPending(null);
  };
  const [gridParams, setGridParams] = useState<GridParams>(DEFAULT_GRID);
  const [gridError, setGridError] = useState<string | null>(null);

  // По живым полкам, а не по rack.shelves из API: иначе «авто-заполнить по сетке» показывалось
  // у стеллажа, у которого полки уже есть (Алексей 14.10, #1549).
  const rackHasShelves = !!editingRack && ((editingRack.shelves?.length ?? 0) > 0
    || Object.values(polygons.shelves.entities).some(sh => String(sh.rack) === String(editingRack.id)));
  const canAutoGrid = selectedType === 'rack' && !rackHasShelves;

  // Returns { shelves } on success or { failed: true } on a degenerate grid.
  // Errors are surfaced via an effect below — never setState during render.
  const gridResult = useMemo((): { shelves: ReturnType<typeof generateShelfGrid> | null; failed: boolean } => {
    if (!gridArmed || !canAutoGrid || !projectionInputs) return { shelves: null, failed: false };
    const pw = Number(form.perspective_width), ph = Number(form.perspective_height);
    if (!pw || !ph) return { shelves: null, failed: false };
    try {
      return { shelves: generateShelfGrid(projectionInputs.pointsNorm, pw, ph, projectionInputs.pixelDims, gridParams), failed: false };
    } catch {
      return { shelves: null, failed: true };
    }
  }, [gridArmed, canAutoGrid, projectionInputs, form.perspective_width, form.perspective_height, gridParams]);
  const generatedShelves = gridResult.shelves;

  useEffect(() => {
    setGridError(gridResult.failed ? t('zoning.grid.error') : null);
  }, [gridResult.failed, t]);

  // Overlay rects in perspective-rect space for the rack CropPreview.
  const gridOverlays = useMemo(() => {
    if (!generatedShelves) return undefined;
    const pw = Number(form.perspective_width), ph = Number(form.perspective_height);
    const pad = gridParams.padding;
    const x0 = pad.left * pw, x1 = (1 - pad.right) * pw;
    const y0 = pad.top * ph, y1 = (1 - pad.bottom) * ph;
    const n = gridParams.count, gap = gridParams.gapPx;
    const rects: { x: number; y: number; w: number; h: number }[] = [];
    if (gridParams.orientation === 'bottom_to_top') {
      const band = ((y1 - y0) - (n - 1) * gap) / n;
      for (let i = 0; i < n; i++) rects.push({ x: x0, y: y0 + i * (band + gap), w: x1 - x0, h: band });
    } else {
      const band = ((x1 - x0) - (n - 1) * gap) / n;
      for (let i = 0; i < n; i++) rects.push({ x: x0 + i * (band + gap), y: y0, w: band, h: y1 - y0 });
    }
    return rects;
  }, [generatedShelves, form.perspective_width, form.perspective_height, gridParams]);

  // ── Delete old entity ──────────────────────────────────────────────────────

  async function deleteOldEntity() {
    const shelves = Object.values(polygons.shelves.entities);
    if (originalType === 'rack' && editingRack) {
      const childShelves = shelves.filter(s => s.rack === editingRack.id);
      for (const s of childShelves) {
        await deleteShelf(s.id);
        try { const { deleteArea } = await import('../api/areas'); await deleteArea(s.area); } catch {}
      }
      // keepArea: полигон стеллажа уходит под новый тип (#3222: иначе 404 «No PolygonArea matches»)
      await deleteRack(editingRack.id, true);
    } else if (originalType === 'shelf' && editingShelf) {
      await deleteShelf(editingShelf.id, true);
    } else if (originalType === 'table' && editingTable) {
      await deleteTable(editingTable.id, true);
    } else if (isCheckoutType(originalType) && editingCheckoutZone) {
      const currentAreaId = resolveAreaId(originalType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone);
      if (editingCheckoutZone.areas.length > 1 && currentAreaId) {
        const updatedAreas = editingCheckoutZone.areas.filter(a => String(a) !== String(currentAreaId));
        await patchPolygonGroup(editingCheckoutZone.group, { areas: updatedAreas });
      } else {
        await deleteCheckoutZone(editingCheckoutZone.id);
        if (editingCheckoutZone.group) {
          try { await deletePolygonGroup(editingCheckoutZone.group); } catch {}
        }
      }
    }
  }

  // ── Create new entity ──────────────────────────────────────────────────────

  async function createNewEntity(areaId: string, type: PolygonEntityType) {
    if (type === 'rack') {
      await createRack({
        area: areaId,
        name: form.name,
        rack_type: form.rack_type || null,
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        initial_width: form.initial_width ? Number(form.initial_width) : null,
        initial_height: form.initial_height ? Number(form.initial_height) : null,
        is_composite: form.is_composite,
        predict_percentage: form.predict_percentage,
        single_shelf: form.single_shelf,
        merchandising_dashboard_id: form.merchandising_dashboard_id || null,
      });
    } else if (type === 'shelf') {
      await createShelf({
        area: areaId,
        rack: selectedRackId || '',
        name: form.name,
        bottom_points_indices: form.bottom_points_indices.split(',').map(s => s.trim()).filter(Boolean).map(Number),
        top_points_indices: form.top_points_indices.split(',').map(s => s.trim()).filter(Boolean).map(Number),
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        empty_shelf_score: form.empty_shelf_score ? Number(form.empty_shelf_score) : null,
        shelf_num_on_rack: form.shelf_num_on_rack ? Number(form.shelf_num_on_rack) : null,
        max_effective_percentage: form.max_effective_percentage ? Number(form.max_effective_percentage) : null,
        predict_percentage: form.shelf_model === '' ? null : form.shelf_model === 'true',
        rack_type: form.shelf_rack_type || null,
      });
    } else if (type === 'table') {
      await createTable({
        area: areaId,
        name: form.name,
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        can_be_physically_moved: form.can_be_physically_moved,
        spoiled_glare_only: form.spoiled_glare_only,
        crop_brighten: form.crop_brighten,
      });
    } else if (isCheckoutType(type)) {
      if (attachMode === 'existing' && attachToZoneId) {
        const existingZone = allCheckoutZones.find(cz => cz.id === attachToZoneId);
        if (existingZone) {
          const updatedAreas = [...existingZone.areas, areaId];
          await patchPolygonGroup(existingZone.group, { areas: updatedAreas });
        }
      } else {
        const czType = ENTITY_TO_CHECKOUT_TYPE[type]!;
        const groupResp = await createPolygonGroup({ name: form.name, areas: [areaId] });
        const groupId = String(groupResp.data.id);
        const czResp = await createCheckoutZone({
          group: groupId,
          name: form.name,
          type: czType,
          operations_dashboard_id: form.operations_dashboard_id || null,
        });
        const czId = String(czResp.data.id);
        if (czType === 'QUEUE') {
          await createCheckoutQueue({ checkout_zone: czId, max_allowed_size: Number(form.max_allowed_size) || 0 });
        }
        if (czType === 'STAFF') {
          await createStaffZone({ checkout_zone: czId, max_workers: Number(form.max_workers) || 0 });
        }
      }
    }
  }

  // ── Patch existing entity (same type) ──────────────────────────────────────

  async function patchExistingEntity() {
    if (selectedType === 'rack' && editingRack) {
      await patchRack(editingRack.id, {
        name: form.name,
        rack_type: form.rack_type || null,
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        initial_width: form.initial_width ? Number(form.initial_width) : null,
        initial_height: form.initial_height ? Number(form.initial_height) : null,
        is_composite: form.is_composite,
        predict_percentage: form.predict_percentage,
        single_shelf: form.single_shelf,
        merchandising_dashboard_id: form.merchandising_dashboard_id || null,
        is_archived: form.is_archived,
      });
    } else if (selectedType === 'shelf' && editingShelf) {
      await patchShelf(editingShelf.id, {
        name: form.name,
        bottom_points_indices: form.bottom_points_indices.split(',').map(s => s.trim()).filter(Boolean).map(Number),
        top_points_indices: form.top_points_indices.split(',').map(s => s.trim()).filter(Boolean).map(Number),
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        empty_shelf_score: form.empty_shelf_score ? Number(form.empty_shelf_score) : null,
        shelf_num_on_rack: form.shelf_num_on_rack ? Number(form.shelf_num_on_rack) : null,
        // Legacy-ключ пишется в model_correction_max_pct — шлём ТОЛЬКО если человек его менял,
        // иначе каждое «Сохранить» полки молча превращало бы конструктор в поправку модели и
        // снимало бы отметку «делитель не подобран» (AI197).
        ...(form.max_effective_percentage !== (editingShelf.max_effective_percentage != null ? String(editingShelf.max_effective_percentage) : '0.8')
          ? { max_effective_percentage: form.max_effective_percentage ? Number(form.max_effective_percentage) : null }
          : {}),
        predict_percentage: form.shelf_model === '' ? null : form.shelf_model === 'true',
        rack_type: form.shelf_rack_type || null,
      });
    } else if (selectedType === 'table' && editingTable) {
      await patchTable(editingTable.id, {
        name: form.name,
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        can_be_physically_moved: form.can_be_physically_moved,
        spoiled_glare_only: form.spoiled_glare_only,
        crop_brighten: form.crop_brighten,
        is_archived: form.is_archived,
      });
    } else if (isCheckoutType(selectedType) && editingCheckoutZone) {
      await patchCheckoutZone(editingCheckoutZone.id, {
        name: form.name,
        operations_dashboard_id: form.operations_dashboard_id || null,
      });
      if (selectedType === 'queue' && editingCheckoutZone.queue) {
        await patchCheckoutQueue(editingCheckoutZone.queue.id, { max_allowed_size: Number(form.max_allowed_size) || 0 });
      }
      if (selectedType === 'staff' && editingCheckoutZone.staffZone) {
        await patchStaffZone(editingCheckoutZone.staffZone.id, { max_workers: Number(form.max_workers) || 0 });
      }
    }
  }

  // ── Shelf-grid creation helpers ─────────────────────────────────────────────

  async function fetchRackByArea(areaId: string) {
    const { listRacksByAreas } = await import('../api/racks');
    const resp = await listRacksByAreas([areaId]);
    const rack = resp.data.results[0];
    if (!rack) throw new Error('Created rack not found by area');
    return rack;
  }

  async function createGridShelves(
    rackId: string,
    shelves: NonNullable<typeof generatedShelves>,
    cameraId: string,
  ) {
    for (const sh of shelves) {
      try {
        const areaResp = await createArea({ direction_vectors: sh.pointsNorm, camera: cameraId, name: sh.name });
        await createShelf({
          area: String(areaResp.data.id),
          rack: rackId,
          name: sh.name,
          bottom_points_indices: sh.bottom_points_indices,
          top_points_indices: sh.top_points_indices,
          perspective_width: sh.perspective_width,
          perspective_height: sh.perspective_height,
          empty_shelf_score: Number(defaultValues.empty_shelf_score),
          shelf_num_on_rack: sh.shelf_num_on_rack,
          max_effective_percentage: Number(defaultValues.max_effective_percentage),
        });
      } catch (e: any) {
        throw new Error(`${t('zoning.grid.shelfFailed')} "${sh.name}": ${e?.message ?? ''}`);
      }
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      if (!form.name) {
        setError(t('zoning.form.errorNameRequired'));
        setSaving(false);
        return;
      }
      if (!selectedCameraId) {
        setError(t('zoning.form.errorCameraNotSelected'));
        setSaving(false);
        return;
      }

      if (isCreateMode) {
        if (isCheckoutType(selectedType) && attachMode === 'existing' && !attachToZoneId) {
          setError(t('zoning.form.errorSelectZone'));
          setSaving(false);
          return;
        }
        if (!frame || !tempPoints || tempPoints.length < 3) {
          setError(t('zoning.form.errorCompleteDrawing'));
          setSaving(false);
          return;
        }
        const normPoints = normalizePoints(tempPoints, { width: frame.width, height: frame.height });
        const areaResp = await createArea({
          direction_vectors: normPoints,
          camera: selectedCameraId,
          name: form.name,
        });
        const areaId = String(areaResp.data.id);
        await createNewEntity(areaId, selectedType);
        if (selectedType === 'rack' && gridArmed && generatedShelves) {
          const newRack = await fetchRackByArea(areaId);
          await createGridShelves(newRack.id, generatedShelves, selectedCameraId);
        }
      } else {
        const areaId = resolveAreaId(originalType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone);
        const typeChanged = selectedType !== originalType;

        if (typeChanged && areaId) {
          await deleteOldEntity();
          await createNewEntity(areaId, selectedType);
        } else {
          await patchExistingEntity();
        }

        if (areaId) {
          await patchArea(areaId, { name: form.name });
        }

        if (!isCreateMode && selectedType === 'rack' && gridArmed && generatedShelves && editingRack) {
          await createGridShelves(editingRack.id, generatedShelves, selectedCameraId);
        }
      }

      // Close the form before reloading. Otherwise the open modal re-renders
      // mid-reload — once the old entity drops out of state but before the
      // new one is in — and the user sees a one-frame flash of the form in
      // an in-between shape (e.g. originalType=queue, editingCheckoutZone
      // already null, so the modal renders as create/standalone for a tick).
      onSuccess?.();
      Promise.all([
        selectedCameraId ? loadPolygonData(selectedCameraId, actions) : null,
        zone.selectedZoneId ? loadZoneSummary(zone.selectedZoneId, actions) : null,
      ].filter(Boolean)).catch(console.error);
    } catch (err: any) {
      console.error('Save failed:', err);
      const detail = err?.response?.data
        ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data))
        : err?.message ?? '';
      setError(`${t('zoning.form.errorSavePrefix')} ${detail}`);
      setSaving(false);
    }
  };

  // ── Computed values ────────────────────────────────────────────────────────

  const entityId = resolveEntityId(originalType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone);
  const areaId = resolveAreaId(originalType, editingRack, editingShelf, editingTable, editingArea, editingCheckoutZone);
  const canSelectShelf = originalType === 'shelf' || !!selectedRackId;

  const selectedIsCheckout = isCheckoutType(selectedType);
  const showAttachOption = selectedIsCheckout && matchingZones.length > 0;
  const showAttachInEdit = !isCreateMode && selectedType !== originalType && showAttachOption;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="relative max-w-md max-h-[90vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="border-b border-border px-6 py-4 mb-0">
          <DialogTitle>
            {isCreateMode ? t('zoning.form.titleCreate') : t('zoning.form.titleEdit')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbar space-y-4 px-6 py-4">
          {/* Info box (edit mode) */}
          {!isCreateMode && (entityId || areaId) && (
            <div className="rounded-md bg-muted px-3 py-2 font-mono text-[11px] text-muted-foreground whitespace-pre-wrap">
              {areaId && `polygon_id: ${areaId}`}
              {entityId && entityId !== areaId && `\nentity_id: ${entityId}`}
              {editingShelf && `\nrack_id: ${editingShelf.rack}`}
              {editingRack?.shelves && editingRack.shelves.length > 0 && `\nshelves: ${editingRack.shelves.join(', ')}`}
              {editingCheckoutZone && `\nzone_id: ${editingCheckoutZone.id}\ngroup_id: ${editingCheckoutZone.group}\nтип: ${t('zoning.types.' + originalType)}`}
            </div>
          )}

          {/* Type selector */}
          <div className="space-y-1">
            <Label htmlFor="f-type">{t('zoning.form.labelType')}</Label>
            <Combobox
              id="f-type"
              value={selectedType}
              onChange={(v) => setSelectedType(v as PolygonEntityType)}
              options={TYPE_OPTIONS.map(opt => ({
                value: opt.value,
                label: t('zoning.types.' + opt.value),
                disabled: opt.value === 'shelf' && !canSelectShelf,
              }))}
            />
          </div>

          <hr className="border-border" />

          {/* Name */}
          <TextField
            label={t('zoning.form.labelName')}
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />

          {/* ── Rack fields ── */}
          {selectedType === 'rack' && (
            <>
              <TextField label={t('zoning.form.labelRackType')} name="rack_type" value={form.rack_type} onChange={handleChange} />
              <PerspectiveFields
                width={form.perspective_width}
                height={form.perspective_height}
                onChange={(f, v) => setField(f, v)}
                onBlur={bumpPreview}
                onRecalc={projectionInputs ? handleRecalcPerspective : undefined}
              />
              <div className="grid grid-cols-2 gap-3">
                <TextField label={t('zoning.form.labelInitialWidth')} name="initial_width" value={form.initial_width} onChange={handleChange} type="number" helper={t('zoning.form.initialDimsHint')} />
                <TextField label={t('zoning.form.labelInitialHeight')} name="initial_height" value={form.initial_height} onChange={handleChange} type="number" />
              </div>
              {projectionInputs && form.perspective_width && form.perspective_height && frame && (
                <div className="space-y-1">
                  <Label>{t('zoning.form.cropPreviewTitle')}</Label>
                  <CropPreview
                    key={previewNonce}
                    imageUrl={frame.url}
                    pointsNorm={projectionInputs.pointsNorm}
                    perspectiveWidth={Number(form.perspective_width)}
                    perspectiveHeight={Number(form.perspective_height)}
                    letterbox
                    overlays={gridOverlays}
                  />
                </div>
              )}
              <CheckRow label={t('zoning.form.labelComposite')} name="is_composite" checked={form.is_composite} onChange={(v) => setField('is_composite', v)} />
              {/* Стеллаж с одной полкой: полка едет за стеллажом (Алексей 14.10, #1547/#1549). */}
              <CheckRow label={t('zoning.form.labelSingleShelf')} name="single_shelf" checked={form.single_shelf} onChange={(v) => setField('single_shelf', v)} />
              <div className="-mt-1 text-[11px] text-muted-foreground">{t('zoning.form.singleShelfHelp')}</div>
              <div className="space-y-0.5 py-1">
                <Label>{t('zoning.form.labelModel')}{rackShelvesConfigured && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">{t('zoning.form.modelPerShelf')}</span>}</Label>
                <RadioRow name="rack_model" value="false" current={String(form.predict_percentage)} onChange={pickRackModel} label={t('zoning.form.modelQuant')} />
                <RadioRow name="rack_model" value="true" current={String(form.predict_percentage)} onChange={pickRackModel} label={t('zoning.form.modelPct')} />
              </div>
              <TextField label={t('zoning.form.labelMerchandisingDashId')} name="merchandising_dashboard_id" value={form.merchandising_dashboard_id} onChange={handleChange} helper={t('zoning.form.labelMerchandisingDashHelper')} />
              {canAutoGrid && (
                <ShelfGridPanel
                  armed={gridArmed}
                  params={gridParams}
                  onArm={() => setGridArmed(true)}
                  onDisarm={() => { setGridArmed(false); setGridError(null); }}
                  onChange={setGridParams}
                  error={gridError}
                />
              )}
              {!isCreateMode && (
                <>
                  <hr className="border-border" />
                  <CheckRow label={t('zoning.form.labelArchived')} name="is_archived" checked={form.is_archived} onChange={(v) => setField('is_archived', v)} />
                  <p className="text-xs text-muted-foreground">{t('zoning.form.archivedHint')}</p>
                </>
              )}
            </>
          )}

          {/* ── Shelf fields ── */}
          {selectedType === 'shelf' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <TextField label={t('zoning.form.labelBottomPoints')} name="bottom_points_indices" value={form.bottom_points_indices} onChange={handleChange} />
                <TextField label={t('zoning.form.labelTopPoints')} name="top_points_indices" value={form.top_points_indices} onChange={handleChange} />
                <TextField label={t('zoning.form.labelEmptyShelfScore')} name="empty_shelf_score" value={form.empty_shelf_score} onChange={handleChange} type="number" />
                <TextField label={t('zoning.form.labelShelfNum')} name="shelf_num_on_rack" value={form.shelf_num_on_rack} onChange={handleChange} type="number" />
              </div>
              <div className="space-y-0.5 py-1">
                <Label>{t('zoning.form.labelModel')}</Label>
                <RadioRow name="shelf_model" value="" current={form.shelf_model} onChange={pickShelfModel} label={t('zoning.form.modelInherit')} />
                <RadioRow name="shelf_model" value="false" current={form.shelf_model} onChange={pickShelfModel} label={t('zoning.form.modelQuant')} />
                <RadioRow name="shelf_model" value="true" current={form.shelf_model} onChange={pickShelfModel} label={t('zoning.form.modelPct')} />
              </div>
              {/* Тип продукции полки поверх стеллажа (Алексей 29.10 #3288): полка бутылок в холодильнике еды — «Напитки» */}
              <div className="grid gap-1.5">
                <Label htmlFor="shelf_rack_type">Тип продукции полки</Label>
                <Select id="shelf_rack_type" value={form.shelf_rack_type} onChange={(e) => setField('shelf_rack_type', e.target.value)}>
                  <option value="">Как у стеллажа</option>
                  {RACK_TYPE_OPTIONS.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                </Select>
              </div>
              <PerspectiveFields
                width={form.perspective_width}
                height={form.perspective_height}
                onChange={(f, v) => setField(f, v)}
                onBlur={bumpPreview}
                onRecalc={projectionInputs ? handleRecalcPerspective : undefined}
              />
              {projectionInputs && form.perspective_width && form.perspective_height && frame && (
                <div className="space-y-1">
                  <Label>{t('zoning.form.cropPreviewTitle')}</Label>
                  <CropPreview key={previewNonce} imageUrl={frame.url} pointsNorm={projectionInputs.pointsNorm}
                    perspectiveWidth={Number(form.perspective_width)} perspectiveHeight={Number(form.perspective_height)} letterbox />
                </div>
              )}
              <TextField label={t('zoning.form.labelMaxEffPct')} name="max_effective_percentage" value={form.max_effective_percentage} onChange={handleChange} type="number" step={0.05} min={0} max={1} helper={t('zoning.form.labelMaxEffPctHelper')} />
              <TextField label={t('zoning.form.labelRackId')} name="rackId" value={editingShelf?.rack || selectedRackId || ''} onChange={() => {}} disabled />
            </>
          )}

          {/* ── Table fields ── */}
          {selectedType === 'table' && (
            <>
              <PerspectiveFields
                width={form.perspective_width}
                height={form.perspective_height}
                onChange={(f, v) => setField(f, v)}
                onBlur={bumpPreview}
                onRecalc={projectionInputs ? handleRecalcPerspective : undefined}
              />
              {projectionInputs && form.perspective_width && form.perspective_height && frame && (
                <div className="space-y-1">
                  <Label>{t('zoning.form.cropPreviewTitle')}</Label>
                  {/* Осветление > 0 — кроп режет бэкенд тем же кодом, что пайплайн (CLAHE в браузере не
                      повторить); 0 — прежний мгновенный предпросмотр в браузере. */}
                  {form.crop_brighten > 0 && frame.id != null ? (
                    <BrightenedCropPreview frameId={frame.id} pointsNorm={projectionInputs.pointsNorm}
                      perspectiveWidth={Number(form.perspective_width)} perspectiveHeight={Number(form.perspective_height)}
                      strength={form.crop_brighten} />
                  ) : (
                    <CropPreview key={previewNonce} imageUrl={frame.url} pointsNorm={projectionInputs.pointsNorm}
                      perspectiveWidth={Number(form.perspective_width)} perspectiveHeight={Number(form.perspective_height)} letterbox={false} />
                  )}
                </div>
              )}
              <div className="space-y-1">
                <Label>{t('zoning.form.cropBrighten')}: {form.crop_brighten.toFixed(1)}</Label>
                <input
                  type="range" min={0} max={1} step={0.1}
                  value={form.crop_brighten}
                  onChange={e => { const v = Number(e.target.value); setForm(prev => ({ ...prev, crop_brighten: v })); }}
                  className="w-full"
                />
                <div className="text-[11px] text-muted-foreground">{t('zoning.form.cropBrightenHelp')}</div>
              </div>
              {/* Clearing this drops the «стол не на месте» state for the object: a bar
                  counter cannot move, so scoring whether it did only produces a yellow
                  band that describes nothing. */}
              <CheckRow
                label={t('zoning.form.canBePhysicallyMoved')}
                name="can_be_physically_moved"
                checked={form.can_be_physically_moved}
                onChange={v => setField('can_be_physically_moved', v)}
              />
              <p className="text-xs text-muted-foreground">
                {t('zoning.form.canBePhysicallyMovedHint')}
              </p>
              {/* Диван с двумя-тремя столами в одном полигоне: LLM всегда видит «несколько
                  столов», карточки валидаторам — шум. С галочкой остаётся только засвет. */}
              <CheckRow
                label={t('zoning.form.spoiledGlareOnly')}
                name="spoiled_glare_only"
                checked={form.spoiled_glare_only}
                onChange={v => setField('spoiled_glare_only', v)}
              />
              <p className="text-xs text-muted-foreground">
                {t('zoning.form.spoiledGlareOnlyHint')}
              </p>
              {!isCreateMode && (
                <>
                  <hr className="border-border" />
                  <CheckRow label={t('zoning.form.labelArchived')} name="is_archived" checked={form.is_archived} onChange={(v) => setField('is_archived', v)} />
                  <p className="text-xs text-muted-foreground">{t('zoning.form.archivedHint')}</p>
                </>
              )}
            </>
          )}

          {/* ── Queue threshold ── */}
          {selectedType === 'queue' && !(attachMode === 'existing' && (isCreateMode || showAttachInEdit)) && (
            <TextField label={t('zoning.form.labelMaxQueueSize')} name="max_allowed_size" value={form.max_allowed_size} onChange={handleChange} type="number" />
          )}

          {/* ── Staff threshold ── */}
          {selectedType === 'staff' && !(attachMode === 'existing' && (isCreateMode || showAttachInEdit)) && (
            <TextField label={t('zoning.form.labelMaxWorkers')} name="max_workers" value={form.max_workers} onChange={handleChange} type="number" />
          )}

          {/* ── Checkout zone: operations dashboard ID ── */}
          {selectedIsCheckout && (
            <TextField label={t('zoning.form.labelOpsDashId')} name="operations_dashboard_id" value={form.operations_dashboard_id} onChange={handleChange} helper={t('zoning.form.labelOpsDashHelper')} />
          )}

          {/* ── Attach to existing zone ── */}
          {((isCreateMode && showAttachOption) || showAttachInEdit) && (
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="text-sm font-medium">{t('zoning.form.sectionAttach')}</div>
              <RadioRow name="attachMode" value="new" current={attachMode} onChange={(v) => setAttachMode(v as 'new' | 'existing')} label={t('zoning.form.radioCreateNew')} />
              <RadioRow name="attachMode" value="existing" current={attachMode} onChange={(v) => setAttachMode(v as 'new' | 'existing')} label={t('zoning.form.radioAddExisting')} />
              {attachMode === 'existing' && (
                <div className="space-y-1">
                  <Label htmlFor="f-attach-zone">{t('zoning.form.labelExistingZone')}</Label>
                  <Combobox
                    id="f-attach-zone"
                    value={attachToZoneId}
                    onChange={setAttachToZoneId}
                    placeholder="—"
                    options={matchingZones.map(cz => ({
                      value: cz.id,
                      label: `${cz.name || t('zoning.form.untitled')} #${cz.id} (${cz.areas.length} ${t('zoning.form.polygonsShort')})`,
                    }))}
                  />
                </div>
              )}
            </div>
          )}

          {/* Type change warning */}
          {!isCreateMode && selectedType !== originalType && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
              {t('zoning.form.typeChangeWarning', { from: t('zoning.types.' + originalType), to: t('zoning.types.' + selectedType) })}
            </div>
          )}

        </div>

        {/* Pinned action bar. The error lives HERE (outside the scroll area),
            not inline with the fields — otherwise on a tall form a validation
            or save error paints at the bottom of the scrolled-away content and
            the user, looking at the pinned Save button, sees "nothing happens". */}
        <div className="shrink-0 space-y-2 border-t border-border px-6 py-3">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t('zoning.form.buttonCancel')}
            </Button>
            <Button type="button" variant="default" onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              {saving ? t('zoning.form.buttonSaving') : t('zoning.form.buttonSave')}
            </Button>
          </div>
        </div>
      </DialogContent>
      {modelPending && (
        <ModelSwitchDialog
          open
          onOpenChange={(v) => { if (!v) setModelPending(null); }}
          scope={modelPending.scope}
          from={modelPending.from}
          to={modelPending.to}
          shelvesConfigured={rackShelvesConfigured}
          onConfirm={confirmModel}
        />
      )}
    </Dialog>
  );
};

export default UnifiedEditForm;
